<template>
  <div :class="$b({ product: isProductImage, lifestyle: !isProductImage })">
    <template v-if="isProductImage">
      <div
        v-show="show360 && activeSlideIndex === 0"
        :class="$b('container', { threeSixty: true })"
      >
        <ThreeSixty
          ref="threeSixty"
          :class="$b('360')"
          :layer-template="layerTemplate"
          :customised-product="customisedProduct"
          :show-iris-preview="showIrisPreview"
          :frame-number-override="current360FrameNumber"
          :view-angle="activeLifestyleIndex"
          @frame-change="(frameNumber) => (current360FrameNumber = frameNumber)"
        />
      </div>
      <div
        ref="productImage"
        :class="$b('container')"
      >
        <HiResLazy v-if="show360 && activeSlideIndex !== 0">
          <StaticThreeSixtyFrame
            :customised-product="customisedProduct"
            :show-iris-preview="showIrisPreview"
            :frame-number="getFrameNumber(activeLifestyleIndex)"
            :show-secondary="
              activeLifestyleIndex === 0 &&
                customisedProduct.conversionKitEnabled
            "
            :class="$b('static-360-frame')"
          />

          <template #hires="{ loadedHandler }">
            <StaticThreeSixtyFrame
              :customised-product="customisedProduct"
              :show-iris-preview="showIrisPreview"
              :frame-number="getFrameNumber(activeLifestyleIndex)"
              :show-secondary="
                activeLifestyleIndex === 0 &&
                  customisedProduct.conversionKitEnabled
              "
              :image-size-override="2100"
              :class="$b('static-360-frame')"
              @loaded="loadedHandler"
            />
          </template>
        </HiResLazy>

        <ProductImg
          v-else
          :view-angle="activeFrameNumber"
          :customised-product="customisedProduct"
          :show-iris-preview="showIrisPreview"
          :show-overflow="true"
          :class="$b('static-image')"
        />
      </div>
    </template>
    <template v-else>
      <div ref="productImage">
        <DatoCmsImage
          v-if="image.responsiveImage.srcSet"
          :class="$b('lifestyle-image')"
          :picture-class="$b('lifestyle-image-picture')"
          :data="image.responsiveImage"
          object-fit="fit"
          :object-position="`${image.focalPoint.x * 100}% ${
            image.focalPoint.y * 100
          }%`"
          :lazy-load="false"
        />
        <img
          v-else
          :class="$b('lifestyle-image')"
          :src="image.src"
          alt=""
        />
      </div>
    </template>
    <div
      v-if="limitedEditionLogo && isProductImage"
      :class="$b('limited-edition-logo')"
    >
      <DatoCmsImage
        v-if="limitedEditionLogo"
        :key="limitedEditionLogo.responsiveImage.src"
        :data="{
          ...limitedEditionLogo.responsiveImage,
          sizes: '(max-width: 800px) 50vw, 400px',
        }"
        :lazy-load="false"
      />
    </div>
  </div>
</template>

<script>
import panzoom from 'panzoom'
import CustomisedProduct from '~/models/CustomisedProduct'
import { Image as DatoCmsImage } from 'vue-datocms'

export default {
  name: 'ProductOrLifestyleImage',

  components: {
    DatoCmsImage,
  },

  props: {
    staticProductAndLifestyleImages: {
      type: Array,
      default: () => [],
    },
    staticProductImages: {
      type: Array,
      default: () => [],
    },
    activeLifestyleIndex: {
      type: Number,
      default: 0,
    },
    activeFrameNumber: {
      type: Number,
      default: 0,
    },
    show360: {
      type: Boolean,
      required: true,
    },
    customisedProduct: {
      type: CustomisedProduct,
      required: true,
    },
    showIrisPreview: {
      type: Boolean,
      default: false,
    },
    activeSlideIndex: {
      type: Number,
      default: 0,
    },
    layerTemplate: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['frame-change'],

  data: () => ({
    current360FrameNumber: 1,
    sizes: '1400px',
    viewAngleMapping: {
      1: 34,
      2: 14,
      3: 58,
      4: 49,
      5: 1,
      6: 46,
    },
    launched360: false,
  }),
  computed: {
    limitedEditionLogo() {
      return this.customisedProduct.product.limitedEditionLogo
    },
    isProductImage() {
      return this.activeLifestyleIndex < this.staticProductImages.length
    },
    image() {
      const index = this.activeLifestyleIndex - this.staticProductImages.length
      return this.staticProductAndLifestyleImages[index]
    },
  },
  watch: {
    current360FrameNumber() {
      this.$emit('frame-change', this.current360FrameNumber)
    },
    async activeSlideIndex() {
      if (this.show360 && this.activeSlideIndex === 0 && !this.launched360) {
        await this.$refs.threeSixty.init()
        this.panzoom.pause()
        return
      }
      this.$nextTick(() => {
        if (this.activeSlideIndex === 0 && this.show360) {
          this.panzoom.pause()
        }

        if (
          this.activeSlideIndex !== 0 &&
          this.$refs.productImage &&
          this.panzoom.isPaused()
        ) {
          this.panzoom.resume()
        }
      })
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.current360FrameNumber =
        this.getFrameNumber(this.activeLifestyleIndex)

      if (this.activeSlideIndex !== 0 && this.$refs.productImage) {
        const windowWidth = window.innerWidth
        const initZoom = windowWidth >= 1300 && !this.show360 ? 1 : 1.35
        const containerWidth =
          this.$refs.productImage.getBoundingClientRect().width
        const containerHeight =
          this.$refs.productImage.getBoundingClientRect().height
        const initOffsetX = (containerWidth / 100) * 50
        const initOffsetY = (containerHeight / 100) * 50
        this.panzoom = panzoom(this.$refs.productImage, {
          minZoom: 1,
          maxZoom: this.setMaxZoom(),
          initialX: initOffsetX,
          initialY: initOffsetY,
          initialZoom: initZoom,
          bounds: true,
          boundsPadding: this.setBoundPadding(),
        })
      }
    })
  },
  methods: {
    getFrameNumber(viewAngle) {
      return this.viewAngleMapping[viewAngle] || 1
    },
    setMaxZoom() {
      return this.image?.width < 1400 ? 1 : 4
    },
    setBoundPadding() {
      return this.image?.width < 1400 ? 1 : 0.75
    },
    animate360Next() {
      this.$refs.threeSixty.animateNext()
    },
    animate360Prev() {
      this.$refs.threeSixty.animatePrev()
    },
    animate360({ frameNumber, animate }) {
      if (animate) this.$refs.threeSixty.animateTo(frameNumber)
      else this.$refs.threeSixty.frameNumber = frameNumber
    },
  },
}
</script>

<style lang="postcss">
.ProductOrLifestyleImage {
  @apply w-full h-full;

  &--lifestyle > div {
    @apply h-full;
  }

  &__container {
    @apply w-full h-full;

    &--three-sixty {
      @apply absolute z-10 bg-grey-200;
    }
  }

  & .ProductOrLifestyleImage__360 {
    @apply absolute inset-0
      grid touch-none;
  }

  &__limited-edition-logo {
    @apply absolute w-1/4 lg:w-1/6
      top-xs left-xs md:top-sm md:left-sm;
    max-width: 280px;

    picture {
      @apply object-cover;
    }
  }

  &__static-360-frame,
  &__lifestyle-image {
    @apply relative mx-auto;
  }

  &__lifestyle-image {
    @apply h-full;
  }

  &__lifestyle-image-picture {
    @apply object-contain;

    img {
      @apply object-contain;
    }
  }
}
</style>
